import { computed, ComputedRef, ref, WritableComputedRef } from "vue";
import type { DSBaseListOption } from "@/src/design_system/form_elements/option_list/types";
import type { DSSelectProps } from "@/src/design_system/form_elements/select/types/props";
import type { DSSelectEmits } from "@/src/design_system/form_elements/select/types/emits";
import { DSOptionValue } from "@/src/design_system/form_elements/shared/types/option";
import { debounce } from "lodash";
import { AUTOCOMPLETE_SEARCH_DEBOUNCE_TIME } from "@/src/design_system/form_elements/select/constants/debounce";

interface UseSelectSearchArgs {
  props: ComputedRef<Readonly<DSSelectProps>>;
  emit: DSSelectEmits;
  displayValue: ComputedRef<string>;
  selectedOptions: ComputedRef<DSBaseListOption[]>;
  removeOption: (value?: DSOptionValue) => void;
  openPopover: () => void;
}

interface UseSelectSearch {
  clearSearch: () => void;
  onBackspace: () => void;
  onInputFocus: (event?: FocusEvent) => void;
  onInputBlur: () => void;
  baseInputModelValue: WritableComputedRef<string | null>;
}

export const useSelectSearch = ({
  props,
  emit,
  displayValue,
  selectedOptions,
  removeOption,
  openPopover,
}: UseSelectSearchArgs): UseSelectSearch => {
  const searchTerm = ref<string | null>(null);

  const debouncedSearchEmit = debounce((val: string) => {
    emit("search", val);
  }, AUTOCOMPLETE_SEARCH_DEBOUNCE_TIME);

  const setSearch = (val: string) => {
    searchTerm.value = val;
    debouncedSearchEmit(val);
    if (props.value.autocomplete) {
      openPopover();
    }
  };

  const clearSearch = () => {
    searchTerm.value = null;
  };

  const onBackspace = () => {
    if (searchTerm.value === "" && props.value.multiple && props.value.autocomplete) {
      removeOption(selectedOptions.value[selectedOptions.value.length - 1]?.value);
    }
  };

  const onInputBlur = () => {
    debouncedSearchEmit.cancel();
    clearSearch();
  };

  const selectInputText = (event?: FocusEvent) => {
    if (searchTerm.value === null && displayValue.value) {
      if (event?.target instanceof HTMLInputElement) event.target.select();
    }
  };

  const emitSearchIfNoOptions = () => {
    if (props.value.options.length === 0) {
      const term = searchTerm.value ?? "";
      emit("search", term);
    }
  };

  const onInputFocus = (event?: FocusEvent) => {
    if (!props.value.autocomplete) return;

    selectInputText(event);
    emitSearchIfNoOptions();
  };

  const resolveBaseInputValue = (): string => {
    const { autocomplete, multiple } = props.value;

    if (!autocomplete) {
      return multiple ? "" : displayValue.value;
    }

    if (searchTerm.value !== null) {
      return searchTerm.value;
    }

    return multiple ? "" : displayValue.value;
  };

  const baseInputModelValue = computed<string | null>({
    get: () => resolveBaseInputValue(),
    set: value => {
      if (!props.value.autocomplete) return;
      setSearch(value ?? "");
    },
  });

  return {
    clearSearch,
    onBackspace,
    baseInputModelValue,
    onInputFocus,
    onInputBlur,
  };
};
